import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Scale, Plus, Upload, ArrowLeft, TrendingUp, TrendingDown, Minus,
  Trash2, X, ChevronDown, ChevronUp,
} from 'lucide-react';
import {
  LineChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis, CartesianGrid,
} from 'recharts';
import { COLORS } from '../data/mockData';
import {
  fetchScaleSummary, createScaleMeasurement,
  deleteScaleMeasurement, importScaleMeasurements,
} from '../services/api';

/**
 * Body Composition page — all ~40 smart-scale metrics, history & manual
 * entry. Designed to round-trip with vendor sync (Withings / Fitbit Aria /
 * RENPHO / Garmin Index) via the /scale/measurements/import endpoint.
 */

// Metric metadata: key -> { label, unit, group }
const GROUPS = [
  {
    name: 'Core Weight & Index',
    fields: [
      ['weight',          'Weight',            'lbs'],
      ['bmi',             'BMI',               ''   ],
      ['obesity_degree',  'Obesity Degree',    '%'  ],
      ['body_type',       'Body Type',         '',  'text'],
    ],
  },
  {
    name: 'Fat Composition',
    fields: [
      ['body_fat_pct',       'Body Fat %',          '%'  ],
      ['fat_mass',           'Fat Mass',            'lbs'],
      ['visceral_fat',       'Visceral Fat',        ''   ],
      ['subcutaneous_fat',   'Subcutaneous Fat',    '%'  ],
      ['body_fat_index',     'Body Fat Index',      ''   ],
      ['target_body_weight', 'Target Body Weight',  'lbs'],
      ['weight_control',     'Weight Control',      'lbs'],
    ],
  },
  {
    name: 'Muscle Mass',
    fields: [
      ['muscle_mass',          'Muscle Mass',           'lbs'],
      ['skeletal_muscle_mass', 'Skeletal Muscle Mass',  'lbs'],
      ['lean_body_mass',       'Lean Body Mass',        'lbs'],
      ['muscle_control',       'Muscle Control',        'lbs'],
      ['muscle_quality_score', 'Muscle Quality Score',  ''   ],
    ],
  },
  {
    name: 'Water',
    fields: [
      ['total_body_water_pct', 'Total Body Water',      '%' ],
      ['intracellular_water',  'Intracellular Water',   'L' ],
      ['extracellular_water',  'Extracellular Water',   'L' ],
      ['water_retention',      'Water Retention',       '' ],
    ],
  },
  {
    name: 'Bone & Protein',
    fields: [
      ['bone_mass',     'Bone Mass',     'lbs'],
      ['bone_density',  'Bone Density',  'g/cm²'],
      ['bone_weight',   'Bone Weight',   'lbs'],
      ['protein_mass',  'Protein Mass',  'lbs'],
      ['protein_pct',   'Protein %',     '%'  ],
    ],
  },
  {
    name: 'Metabolic & Caloric',
    fields: [
      ['bmr',                  'BMR',             'kcal'],
      ['rmr',                  'RMR',             'kcal'],
      ['metabolic_age',        'Metabolic Age',   'yrs' ],
      ['daily_caloric_intake', 'Daily Calorie Goal', 'kcal'],
      ['calorie_balance',      'Calorie Balance', 'kcal'],
    ],
  },
  {
    name: 'Advanced',
    fields: [
      ['body_score',          'Body Score',          ''   ],
      ['biological_age',      'Biological Age',      'yrs'],
      ['pulse_wave_velocity', 'Pulse Wave Velocity', 'm/s'],
      ['heart_rate',          'Heart Rate',          'bpm'],
    ],
  },
];

const SEGMENTAL_FIELDS = [
  ['left_arm_fat',    'Left Arm Fat',     'lbs'],
  ['left_arm_muscle', 'Left Arm Muscle',  'lbs'],
  ['right_arm_fat',   'Right Arm Fat',    'lbs'],
  ['right_arm_muscle','Right Arm Muscle', 'lbs'],
  ['left_leg_fat',    'Left Leg Fat',     'lbs'],
  ['left_leg_muscle', 'Left Leg Muscle',  'lbs'],
  ['right_leg_fat',   'Right Leg Fat',    'lbs'],
  ['right_leg_muscle','Right Leg Muscle', 'lbs'],
  ['trunk_fat',       'Trunk Fat',        'lbs'],
  ['trunk_muscle',    'Trunk Muscle',     'lbs'],
];

// Key cards (highlighted at the top)
const KEY_CARDS = [
  ['weight',           'Weight',          'lbs'],
  ['body_fat_pct',     'Body Fat',        '%'  ],
  ['muscle_mass',      'Muscle Mass',     'lbs'],
  ['visceral_fat',     'Visceral Fat',    ''   ],
  ['bmr',              'BMR',             'kcal'],
  ['biological_age',   'Bio Age',         'yrs'],
];


function fmt(v, digits = 1) {
  if (v === null || v === undefined || v === '' || Number.isNaN(v)) return '—';
  if (typeof v === 'string') return v;
  return (Math.round(v * Math.pow(10, digits)) / Math.pow(10, digits)).toLocaleString();
}

function DeltaTag({ delta, lowerIsBetter = false }) {
  if (delta === undefined || delta === null || delta === 0) {
    return <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2, fontSize: 11, color: COLORS.textSecondary }}><Minus size={11} /> –</span>;
  }
  const isImprovement = lowerIsBetter ? delta < 0 : delta > 0;
  const color = isImprovement ? COLORS.success : COLORS.error;
  const Icon = delta > 0 ? TrendingUp : TrendingDown;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2, fontSize: 11, color, fontWeight: 700 }}>
      <Icon size={11} /> {delta > 0 ? '+' : ''}{fmt(delta, 1)}
    </span>
  );
}

const LOWER_IS_BETTER = new Set([
  'weight', 'bmi', 'obesity_degree', 'body_fat_pct', 'fat_mass',
  'visceral_fat', 'subcutaneous_fat', 'body_fat_index', 'water_retention',
  'metabolic_age', 'biological_age', 'pulse_wave_velocity',
]);


export default function BodyCompositionPage() {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [openSegmental, setOpenSegmental] = useState(false);

  const load = () => {
    setLoading(true);
    fetchScaleSummary()
      .then((d) => { setSummary(d); setError(null); })
      .catch((e) => setError(e?.message || 'Failed to load measurements'))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const latest = summary?.latest;
  const deltas = summary?.deltas || {};
  const history = summary?.history || [];

  const weightSeries = useMemo(
    () => history.filter((r) => r.weight != null).map((r) => ({
      date: (r.measured_at || '').slice(0, 10),
      weight: r.weight,
      fat: r.body_fat_pct,
      muscle: r.muscle_mass,
    })),
    [history]
  );

  const onDelete = async (id) => {
    if (!confirm('Delete this measurement?')) return;
    await deleteScaleMeasurement(id);
    load();
  };

  return (
    <div data-testid="body-composition-page" style={{ maxWidth: 980, margin: '0 auto', padding: '16px 24px 40px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
        <button onClick={() => navigate('/dashboard/profile')} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
          <ArrowLeft size={22} color={COLORS.textPrimary} />
        </button>
        <Scale size={26} color={COLORS.primary} />
        <h1 style={{ fontSize: 24, fontWeight: 800, color: COLORS.textPrimary, margin: 0, fontFamily: 'Outfit, sans-serif' }}>Body Composition</h1>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          <ImportButton onDone={load} />
          <button
            data-testid="add-measurement-btn"
            onClick={() => setShowAdd(true)}
            style={{ padding: '10px 16px', borderRadius: 10, border: 'none', cursor: 'pointer', backgroundColor: COLORS.primary, color: '#FFF', fontSize: 13, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6 }}
          >
            <Plus size={16} /> Add Measurement
          </button>
        </div>
      </div>

      {loading && <p style={{ color: COLORS.textSecondary }}>Loading…</p>}
      {error && <p style={{ color: COLORS.error }}>{error}</p>}

      {!loading && !latest && (
        <div style={{ textAlign: 'center', padding: '48px 16px', backgroundColor: COLORS.surface, borderRadius: 18 }}>
          <Scale size={48} color={COLORS.border} />
          <h2 style={{ fontSize: 19, fontWeight: 700, color: COLORS.textPrimary, marginTop: 10, fontFamily: 'Outfit, sans-serif' }}>No measurements yet</h2>
          <p style={{ fontSize: 14, color: COLORS.textSecondary, maxWidth: 380, margin: '8px auto 18px' }}>
            Sync your smart scale via the Wearables page or hand-enter your first reading to start tracking 40+ body-composition metrics over time.
          </p>
          <button
            onClick={() => setShowAdd(true)}
            style={{ padding: '12px 22px', borderRadius: 9999, border: 'none', cursor: 'pointer', backgroundColor: COLORS.primary, color: '#FFF', fontSize: 14, fontWeight: 700 }}
          >
            Add First Measurement
          </button>
        </div>
      )}

      {latest && (
        <>
          {/* Key metric cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 10, marginBottom: 16 }}>
            {KEY_CARDS.map(([k, label, unit]) => (
              <div key={k} data-testid={`metric-card-${k}`} style={{ backgroundColor: COLORS.surface, borderRadius: 14, padding: 14, boxShadow: '0 1px 6px rgba(0,0,0,0.04)' }}>
                <div style={{ fontSize: 11, color: COLORS.textSecondary, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 4, marginTop: 4 }}>
                  <div style={{ fontSize: 22, fontWeight: 800, color: COLORS.textPrimary, fontFamily: 'Outfit, sans-serif' }}>{fmt(latest[k])}</div>
                  {unit && <div style={{ fontSize: 11, color: COLORS.textSecondary }}>{unit}</div>}
                </div>
                <div style={{ marginTop: 4 }}>
                  <DeltaTag delta={deltas[k]} lowerIsBetter={LOWER_IS_BETTER.has(k)} />
                </div>
              </div>
            ))}
          </div>

          {/* Trend chart */}
          {weightSeries.length > 1 && (
            <div style={{ backgroundColor: COLORS.surface, borderRadius: 18, padding: 18, marginBottom: 16 }}>
              <h3 style={{ fontSize: 15, fontWeight: 700, color: COLORS.textPrimary, marginBottom: 8, fontFamily: 'Outfit, sans-serif' }}>Weight, Body Fat % &amp; Muscle Mass Trend</h3>
              <div style={{ width: '100%', height: 220 }}>
                <ResponsiveContainer>
                  <LineChart data={weightSeries} margin={{ top: 6, right: 18, bottom: 0, left: 0 }}>
                    <CartesianGrid stroke={`${COLORS.border}80`} vertical={false} />
                    <XAxis dataKey="date" tick={{ fontSize: 10, fill: COLORS.textSecondary }} />
                    <YAxis tick={{ fontSize: 10, fill: COLORS.textSecondary }} />
                    <Tooltip contentStyle={{ backgroundColor: '#2F3E46', border: 'none', borderRadius: 10, color: '#FFF', fontSize: 12 }} />
                    <Line dataKey="weight" stroke="#6B8E23" strokeWidth={2.2} dot={false} name="Weight" />
                    <Line dataKey="fat" stroke="#EF5350" strokeWidth={2} dot={false} name="Body Fat %" />
                    <Line dataKey="muscle" stroke="#4682B4" strokeWidth={2} dot={false} name="Muscle Mass" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* Full metric breakdown grouped */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12, marginBottom: 16 }}>
            {GROUPS.map((g) => (
              <div key={g.name} data-testid={`group-${g.name.toLowerCase().replace(/\W+/g,'-')}`} style={{ backgroundColor: COLORS.surface, borderRadius: 14, padding: 14, boxShadow: '0 1px 6px rgba(0,0,0,0.04)' }}>
                <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: 1, color: COLORS.primary, textTransform: 'uppercase', marginBottom: 8 }}>{g.name}</div>
                {g.fields.map(([k, label, unit]) => (
                  <div key={k} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0', borderBottom: `1px solid ${COLORS.border}40` }}>
                    <span style={{ fontSize: 13, color: COLORS.textSecondary }}>{label}</span>
                    <span style={{ fontSize: 13, fontWeight: 700, color: COLORS.textPrimary }}>{fmt(latest[k])}{unit && <span style={{ fontSize: 11, fontWeight: 400, color: COLORS.textSecondary, marginLeft: 2 }}>{unit}</span>}</span>
                  </div>
                ))}
              </div>
            ))}
          </div>

          {/* Segmental */}
          {latest.segmental && Object.values(latest.segmental).some((v) => v != null) && (
            <div style={{ backgroundColor: COLORS.surface, borderRadius: 14, padding: 14, marginBottom: 16 }}>
              <button onClick={() => setOpenSegmental(!openSegmental)} style={{ background: 'none', border: 'none', cursor: 'pointer', width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: 0 }}>
                <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: 1, color: COLORS.primary, textTransform: 'uppercase' }}>Segmental Analysis (5-Zone)</div>
                {openSegmental ? <ChevronUp size={18} color={COLORS.primary} /> : <ChevronDown size={18} color={COLORS.primary} />}
              </button>
              {openSegmental && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))', gap: 8, marginTop: 10 }}>
                  {SEGMENTAL_FIELDS.map(([k, label, unit]) => (
                    <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderBottom: `1px solid ${COLORS.border}40` }}>
                      <span style={{ fontSize: 12, color: COLORS.textSecondary }}>{label}</span>
                      <span style={{ fontSize: 12, fontWeight: 700, color: COLORS.textPrimary }}>{fmt(latest.segmental[k])}<span style={{ fontSize: 10, color: COLORS.textSecondary, marginLeft: 2 }}>{unit}</span></span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* History list */}
          <h3 style={{ fontSize: 15, fontWeight: 700, color: COLORS.textPrimary, margin: '8px 0 8px', fontFamily: 'Outfit, sans-serif' }}>Recent Readings</h3>
          <div style={{ backgroundColor: COLORS.surface, borderRadius: 14, overflow: 'hidden' }}>
            {[...history].reverse().slice(0, 20).map((r) => (
              <div key={r.measurement_id} data-testid={`history-row-${r.measurement_id}`} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderBottom: `1px solid ${COLORS.border}40` }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.textPrimary }}>{(r.measured_at || '').replace('T',' ').slice(0,16)}</div>
                  <div style={{ fontSize: 12, color: COLORS.textSecondary, marginTop: 2 }}>
                    Wt {fmt(r.weight)} lbs · Fat {fmt(r.body_fat_pct)}% · Muscle {fmt(r.muscle_mass)} lbs · Source: {r.source || 'manual'}
                  </div>
                </div>
                <button onClick={() => onDelete(r.measurement_id)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 6 }}>
                  <Trash2 size={16} color={COLORS.error} />
                </button>
              </div>
            ))}
          </div>
        </>
      )}

      {showAdd && <AddMeasurementModal onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); load(); }} />}
    </div>
  );
}


function ImportButton({ onDone }) {
  const onPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      let parsed;
      try {
        parsed = JSON.parse(text);
      } catch {
        alert('Import file must be JSON: either an array of measurements or { measurements: [ … ] }');
        return;
      }
      const measurements = Array.isArray(parsed) ? parsed : (parsed.measurements || []);
      if (!Array.isArray(measurements) || measurements.length === 0) {
        alert('No measurements found in file');
        return;
      }
      const res = await importScaleMeasurements(measurements);
      alert(`Imported ${res.imported} measurement(s)`);
      onDone?.();
    } catch (err) {
      console.error(err);
      alert('Import failed: ' + (err?.response?.data?.detail || err.message));
    } finally {
      e.target.value = '';
    }
  };

  return (
    <label data-testid="import-measurements-btn" style={{ padding: '10px 14px', borderRadius: 10, border: `1px solid ${COLORS.border}`, cursor: 'pointer', backgroundColor: COLORS.surface, color: COLORS.textPrimary, fontSize: 13, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <Upload size={15} /> Import
      <input type="file" accept=".json,application/json" onChange={onPick} style={{ display: 'none' }} />
    </label>
  );
}


function AddMeasurementModal({ onClose, onSaved }) {
  const [vals, setVals] = useState({});
  const [seg, setSeg] = useState({});
  const [showSeg, setShowSeg] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  const set = (k, v) => setVals({ ...vals, [k]: v });
  const setS = (k, v) => setSeg({ ...seg, [k]: v });

  const save = async () => {
    setSaving(true); setErr(null);
    try {
      const payload = {};
      // Convert numeric strings to numbers; drop empties.
      for (const [k, v] of Object.entries(vals)) {
        if (v === '' || v === null || v === undefined) continue;
        const n = Number(v);
        payload[k] = Number.isFinite(n) ? n : v;
      }
      const segPayload = {};
      for (const [k, v] of Object.entries(seg)) {
        if (v === '' || v === null || v === undefined) continue;
        const n = Number(v);
        if (Number.isFinite(n)) segPayload[k] = n;
      }
      if (Object.keys(segPayload).length) payload.segmental = segPayload;
      await createScaleMeasurement(payload);
      onSaved?.();
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', overflowY: 'auto', padding: '40px 16px' }}>
      <div onClick={(e) => e.stopPropagation()} data-testid="add-measurement-modal" style={{ backgroundColor: COLORS.background, borderRadius: 18, width: '100%', maxWidth: 760, padding: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h2 style={{ fontSize: 19, fontWeight: 800, color: COLORS.textPrimary, margin: 0, fontFamily: 'Outfit, sans-serif' }}>New Measurement</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={22} color={COLORS.textPrimary} /></button>
        </div>

        {GROUPS.map((g) => (
          <fieldset key={g.name} style={{ border: `1px solid ${COLORS.border}`, borderRadius: 10, padding: 12, marginBottom: 12 }}>
            <legend style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1, color: COLORS.primary, textTransform: 'uppercase', padding: '0 6px' }}>{g.name}</legend>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 8 }}>
              {g.fields.map(([k, label, unit, type]) => (
                <label key={k} style={{ display: 'flex', flexDirection: 'column', gap: 3, fontSize: 12, color: COLORS.textSecondary }}>
                  <span>{label}{unit ? ` (${unit})` : ''}</span>
                  <input
                    type={type === 'text' ? 'text' : 'number'}
                    step="any"
                    value={vals[k] ?? ''}
                    onChange={(e) => set(k, e.target.value)}
                    style={{ padding: 8, borderRadius: 8, border: `1px solid ${COLORS.border}`, fontSize: 13, backgroundColor: COLORS.surface }}
                  />
                </label>
              ))}
            </div>
          </fieldset>
        ))}

        <fieldset style={{ border: `1px solid ${COLORS.border}`, borderRadius: 10, padding: 12, marginBottom: 12 }}>
          <legend style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1, color: COLORS.primary, textTransform: 'uppercase', padding: '0 6px' }}>
            <button type="button" onClick={() => setShowSeg(!showSeg)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontWeight: 700, color: COLORS.primary, letterSpacing: 1, textTransform: 'uppercase', fontSize: 11, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              Segmental Analysis (5-Zone)  {showSeg ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </button>
          </legend>
          {showSeg && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))', gap: 8 }}>
              {SEGMENTAL_FIELDS.map(([k, label, unit]) => (
                <label key={k} style={{ display: 'flex', flexDirection: 'column', gap: 3, fontSize: 12, color: COLORS.textSecondary }}>
                  <span>{label} ({unit})</span>
                  <input
                    type="number"
                    step="any"
                    value={seg[k] ?? ''}
                    onChange={(e) => setS(k, e.target.value)}
                    style={{ padding: 8, borderRadius: 8, border: `1px solid ${COLORS.border}`, fontSize: 13, backgroundColor: COLORS.surface }}
                  />
                </label>
              ))}
            </div>
          )}
        </fieldset>

        {err && <div style={{ color: COLORS.error, fontSize: 13, marginBottom: 8 }}>{err}</div>}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
          <button onClick={onClose} style={{ padding: '10px 16px', borderRadius: 10, border: `1px solid ${COLORS.border}`, backgroundColor: COLORS.surface, cursor: 'pointer', fontSize: 13, fontWeight: 600, color: COLORS.textPrimary }}>Cancel</button>
          <button data-testid="save-measurement-btn" onClick={save} disabled={saving} style={{ padding: '10px 18px', borderRadius: 10, border: 'none', backgroundColor: COLORS.primary, color: '#FFF', cursor: 'pointer', fontSize: 13, fontWeight: 700, opacity: saving ? 0.7 : 1 }}>{saving ? 'Saving…' : 'Save Measurement'}</button>
        </div>
      </div>
    </div>
  );
}
